import { motion } from 'framer-motion'
import { CreditCard, Link, BarChart3 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { profile } from '@/data/profile'

const highlights = [
  { label: 'Live Projects', value: '3+' },
  { label: 'Projects Completed', value: `${profile.highlights.projects}+` },
  { label: 'Focus Areas', value: profile.highlights.focus.length.toString() },
]

const focusAreas = [
  {
    title: 'Financial Technology',
    description: 'Building scalable fintech solutions and payment systems',
    icon: <CreditCard className="h-8 w-8" />,
  },
  {
    title: 'Cryptocurrency & DeFi',
    description: 'Developing smart contracts and DeFi protocols',
    icon: <Link className="h-8 w-8" />,
  },
  {
    title: 'Data Science & ML',
    description: 'Creating predictive models and data-driven insights',
    icon: <BarChart3 className="h-8 w-8" />,
  },
]

export function About() {
  return (
    <section id="about" className="py-20 bg-white/30 dark:bg-bg-dark/30">
      <div className="container-max section-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          {/* Section header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-light dark:text-text-dark mb-4">
              About Me
            </h2>
            <p className="text-lg text-text-light/70 dark:text-text-dark/70 max-w-2xl mx-auto">
              A brief look into my journey and what drives my passion for technology
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Brief description */}
              <div className="border-l-4 border-accent pl-6">
                <p className="text-xl font-normal text-text-light dark:text-text-dark leading-relaxed" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  Computer science student focused on the intersection of traditional finance and blockchain technology.
                </p>
              </div>
              
              {/* Degree information */}
              <div className="flex items-center space-x-2 text-lg font-light text-text-light/70 dark:text-text-dark/70" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                <div className="w-1 h-1 bg-accent rounded-full"></div>
                <span>B.S. Computer Science @ NYU</span>
              </div>

              {/* Expertise section */}
              <div className="space-y-6">
                <h3 className="text-lg font-normal text-accent uppercase tracking-wider relative" style={{ fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '0.15em' }}>
                  <span className="relative z-10">EXPERTISE</span>
                  <div className="absolute -bottom-1 left-0 w-16 h-0.5 bg-gradient-to-r from-accent to-accent-contrast"></div>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Computer Science</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>DeFi</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Full-Stack Dev</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Open Source</div>
                  </div>
                  <div className="space-y-3">
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Blockchain</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Cryptocurrency</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Financial Tech</div>
                    <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>Market Analysis</div>
                  </div>
                </div>
              </div>

              {/* Current section */}
              <div className="space-y-6">
                <h3 className="text-lg font-normal text-accent uppercase tracking-wider relative" style={{ fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '0.15em' }}>
                  <span className="relative z-10">CURRENT</span>
                  <div className="absolute -bottom-1 left-0 w-16 h-0.5 bg-gradient-to-r from-accent to-accent-contrast"></div>
                </h3>
                <div className="space-y-3">
                  <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default flex items-center" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    <div className="w-2 h-2 bg-accent rounded-full mr-3 animate-pulse"></div>
                    Building fintech solutions
                  </div>
                  <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default flex items-center" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    <div className="w-2 h-2 bg-accent-contrast rounded-full mr-3 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                    Contributing to open source
                  </div>
                  <div className="text-lg font-light text-text-light dark:text-text-dark hover:text-accent transition-colors duration-200 cursor-default flex items-center" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    <div className="w-2 h-2 bg-accent rounded-full mr-3 animate-pulse" style={{ animationDelay: '1s' }}></div>
                    Exploring DeFi protocols
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Portrait and highlights */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Portrait card */}
              <Card className="p-0 overflow-hidden group">
                <div className="aspect-square relative">
                  <img
                    src="/profile.png"
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay gradient for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  {/* Decorative border */}
                  <div className="absolute inset-0 border-4 border-accent/20 dark:border-accent/30"></div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-2">
                    Always Learning & Sailing
                  </h3>
                  <p className="text-text-light/70 dark:text-text-dark/70">
                    Continuously exploring new technologies and methodologies
                  </p>
                </CardContent>
              </Card>

              {/* Highlights grid */}
              <div className="grid grid-cols-3 gap-4">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={highlight.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="text-2xl font-bold text-accent mb-1">
                      {highlight.value}
                    </div>
                    <div className="text-sm text-text-light/70 dark:text-text-dark/70">
                      {highlight.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Focus areas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <h3 className="text-2xl font-semibold text-text-light dark:text-text-dark text-center mb-8">
              Focus Areas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {focusAreas.map((area, index) => (
                <motion.div
                  key={area.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="h-full"
                >
                  <Card className="text-center group hover:card-hover h-full flex flex-col">
                    <CardContent className="p-6 flex flex-col flex-1">
                      <div className="text-accent/60 mb-4 flex justify-center">{area.icon}</div>
                      <h4 className="text-lg font-semibold text-text-light dark:text-text-dark mb-2">
                        {area.title}
                      </h4>
                      <p className="text-text-light/70 dark:text-text-dark/70 flex-1">
                        {area.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
